import { useMemo, useState } from 'react'
import { BadgeCheck, ChevronDown, ExternalLink, Phone, Scale, Search, ShieldAlert, Volume2 } from 'lucide-react'
import type { LanguagePack } from '../i18n/packs'
import { languageInfo } from '../i18n/languages'
import type { useSpeech } from '../hooks/useSpeech'
import { legalCopy } from '../legal'
import { HELPLINES, LEGAL_TOPICS } from '../legal/topics'
import { askConfigured } from '../services/ask'
import { displayHost } from '../shared/trusted-sources'
import type { Language } from '../types/document'
import { AskPanel } from './AskPanel'
import { LanguagePicker } from './LanguagePicker'

type Speech = ReturnType<typeof useSpeech>

/** Kanooni Madad: common legal questions (read or listen), helplines, and Ask on official legal sources. */
export function LegalHelp({
  p,
  language,
  onLanguage,
  speech,
  onShare
}: {
  p: LanguagePack
  language: Language
  onLanguage: (language: Language) => void
  speech: Speech
  onShare: (text: string) => void
}) {
  const copy = legalCopy(language)
  const english = legalCopy('en')
  const [filter, setFilter] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  // Search matches the chosen language and English, so "FIR" or "RTI" typed in English still finds the answer
  const topics = useMemo(() => {
    const words = filter.toLowerCase().split(/\s+/).filter(Boolean)
    return LEGAL_TOPICS.filter((topic) => {
      if (words.length === 0) return true
      const text = `${copy.topics[topic.id].q} ${copy.topics[topic.id].a} ${english.topics[topic.id].q} ${topic.keywords}`.toLowerCase()
      return words.every((w) => text.includes(w))
    })
  }, [filter, copy, english])

  return (
    <section className="legal-view">
      <div className="result-topbar">
        <span />
        <LanguagePicker id="legal-language" compact label="" value={language} onChange={onLanguage} />
      </div>

      <header className="legal-hero">
        <span className="legal-badge" aria-hidden="true">
          <Scale size={28} />
        </span>
        <h1>{copy.title}</h1>
        <p>{copy.subtitle}</p>
      </header>

      <section className="legal-helplines" aria-labelledby="helplines-title">
        <h2 id="helplines-title">
          <ShieldAlert size={20} aria-hidden="true" /> {copy.helplinesTitle}
        </h2>
        <div className="helpline-grid">
          {HELPLINES.map((line) => (
            <a key={line.id} className={`helpline helpline-${line.id}`} href={`tel:${line.number}`}>
              <span className="helpline-number">
                <Phone size={16} aria-hidden="true" /> {line.number}
              </span>
              <span className="helpline-label">{copy.helplines[line.id]}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="legal-faqs" aria-labelledby="faq-title">
        <h2 id="faq-title">{copy.faqTitle}</h2>
        <label className="legal-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            value={filter}
            placeholder={copy.searchPlaceholder}
            aria-label={copy.searchPlaceholder}
            onChange={(e) => setFilter(e.target.value)}
            lang={languageInfo(language).bcp47}
          />
        </label>

        {topics.length === 0 && <p className="speech-note">{copy.noMatch}</p>}

        <ul className="legal-list">
          {topics.map((topic) => {
            const text = copy.topics[topic.id]
            const expanded = open === topic.id
            return (
              <li key={topic.id} className={`legal-item ${expanded ? 'open' : ''}`}>
                <button className="legal-question" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : topic.id)}>
                  <span className="legal-icon" aria-hidden="true">{topic.icon}</span>
                  <span className="legal-q" lang={languageInfo(language).bcp47}>{text.q}</span>
                  <ChevronDown size={20} aria-hidden="true" className="legal-chevron" />
                </button>
                {expanded && (
                  <div className="legal-answer">
                    <p lang={languageInfo(language).bcp47}>{text.a}</p>
                    <div className="help-actions">
                      {speech.supported && speech.hasVoice(language) && (
                        <button className="inline-action" onClick={() => speech.play([text.q, text.a], language)}>
                          <Volume2 size={16} aria-hidden="true" /> {copy.listen}
                        </button>
                      )}
                      {topic.actions.map((action) => (
                        <a
                          key={action.href}
                          className="inline-action"
                          href={action.href}
                          {...(action.href.startsWith('tel:') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                        >
                          {action.href.startsWith('tel:') ? <Phone size={16} aria-hidden="true" /> : <ExternalLink size={16} aria-hidden="true" />}
                          {action.label}
                        </a>
                      ))}
                    </div>
                    <p className="legal-sources">
                      <span>{copy.sources}:</span>
                      {topic.sources.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                          <BadgeCheck size={14} aria-hidden="true" /> {displayHost(url)}
                        </a>
                      ))}
                    </p>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      {askConfigured() && (
        <AskPanel
          key={`legal-${language}`}
          p={p}
          language={language}
          doc={null}
          ocr={null}
          speech={speech}
          onShare={onShare}
          title={copy.askTitle}
          hint={copy.askHint}
          starters={copy.starters}
        />
      )}

      <div className="disclaimer-box">
        <Scale size={20} aria-hidden="true" />
        <p>{copy.disclaimer}</p>
      </div>
    </section>
  )
}
